import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Button,
  useTheme,
  alpha,
  Tooltip,
  Zoom,
} from '@mui/material'
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  KeyboardArrowRight as ArrowIcon,
  AccessTime as ClockIcon,
  Language as LanguageIcon,
  Star as StarIcon,
} from '@mui/icons-material'
import Logo from '../Logo'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'

const Footer = () => {
  const theme = useTheme()

  const MotionStack = motion(Stack)
  const MotionBox = motion(Box)

  const FooterLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
    <Link
      to={to}
      style={{ textDecoration: 'none' }}
    >
      <MotionStack
        direction="row"
        alignItems="center"
        spacing={1}
        initial={{ x: 0 }}
        whileHover={{ x: 8, transition: { duration: 0.2 } }}
        sx={{
          color: 'grey.300',
          '&:hover': {
            color: 'white',
            '& .arrow': {
              opacity: 1,
              transform: 'translateX(0)',
            },
          },
        }}
      >
        <Typography variant="body2">
          {children}
        </Typography>
        <ArrowIcon
          className="arrow"
          sx={{
            fontSize: 16,
            opacity: 0,
            transform: 'translateX(-4px)',
            transition: 'all 0.2s ease',
          }}
        />
      </MotionStack>
    </Link>
  )

  const SocialButton = ({ icon, href, label }: { icon: React.ReactNode; href: string; label: string }) => (
    <Tooltip 
      title={label} 
      placement="top" 
      TransitionComponent={Zoom}
      arrow
    >
      <MotionBox
        component={motion.div}
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.95 }}
        transition={{ type: "spring", stiffness: 400, damping: 10 }}
      >
        <IconButton
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: 'white',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            '&:hover': {
              bgcolor: 'primary.main',
            },
          }}
        >
          {icon}
        </IconButton>
      </MotionBox>
    </Tooltip>
  )

  const InfoCard = ({ icon, title, content }: { icon: React.ReactNode; title: string; content: React.ReactNode }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="flex-start"
        sx={{
          p: 2,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.common.white, 0.03),
          border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
          '&:hover': {
            bgcolor: alpha(theme.palette.common.white, 0.05),
            borderColor: alpha(theme.palette.common.white, 0.2),
          },
        }}
      >
        <IconButton
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) },
          }}
        >
          {icon}
        </IconButton>
        <Box>
          <Typography variant="subtitle2" sx={{ color: 'grey.100', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.400' }}>
            {content}
          </Typography>
        </Box>
      </Stack>
    </motion.div>
  )

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      sx={{
        bgcolor: 'grey.900',
        color: 'white',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, 
            ${alpha(theme.palette.primary.main, 0)}, 
            ${alpha(theme.palette.primary.main, 0.3)}, 
            ${alpha(theme.palette.primary.main, 0)})`,
        },
      }}
    >
      {/* Main Footer Content */}
      <Container maxWidth="xl">
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Grid container spacing={4}>
            {/* Brand Section */}
            <Grid item xs={12} md={5}>
              <MotionBox
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                sx={{ mb: 3 }}
              >
                <Logo color="white" textColor="white" />
              </MotionBox>
              <Typography
                variant="body2"
                sx={{
                  color: 'grey.400',
                  mb: 4,
                  maxWidth: 400,
                  lineHeight: 1.8,
                }}
              >
                Experience unparalleled luxury and comfort at Bisworupa Inn, Puri's premier hotel destination. Located near the iconic Light House, we offer the perfect blend of modern amenities and traditional hospitality.
              </Typography>
              <Stack direction="row" spacing={2}>
                <SocialButton
                  icon={<FacebookIcon />}
                  href="https://www.facebook.com/people/Hotel-Bisworupa-Inn/61573716703811/"
                  label="Follow us on Facebook"
                />
                <SocialButton
                  icon={<InstagramIcon />}
                  href="https://instagram.com/bisworupainn"
                  label="Follow us on Instagram"
                />
                <SocialButton
                  icon={<TwitterIcon />}
                  href="https://twitter.com/bisworupainn"
                  label="Follow us on Twitter"
                />
                <SocialButton
                  icon={<WhatsAppIcon />}
                  href="https://wa.me/917978190633"
                  label="Chat with us on WhatsApp"
                />
              </Stack>
            </Grid>

            {/* Quick Links */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  mb: 3,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: 40,
                    height: 2,
                    bgcolor: 'primary.main',
                  },
                }}
              >
                Quick Links
              </Typography>
              <Stack spacing={2}>
                <FooterLink to="/">Home</FooterLink>
                <FooterLink to="/rooms">Our Rooms</FooterLink>
                <FooterLink to="/contact">Contact Us</FooterLink>
                <FooterLink to="/gallery">Gallery</FooterLink>
                <FooterLink to="/about">About Us</FooterLink>
              </Stack>
            </Grid>

            {/* Legal Links */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  mb: 3,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: 40,
                    height: 2,
                    bgcolor: 'primary.main',
                  },
                }}
              >
                Legal
              </Typography>
              <Stack spacing={2}>
                <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
                <FooterLink to="/terms-and-conditions">Terms & Conditions</FooterLink>
                <FooterLink to="/cancellation-policy">Cancellation Policy</FooterLink>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'grey.400',
                    mt: 2,
                  }}
                >
                  Check-in: 12:00 PM | Check-out: 11:00 AM
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Bottom Bar */}
      <Box
        sx={{
          borderTop: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
          py: 3,
          bgcolor: alpha(theme.palette.common.black, 0.3),
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="body2"
                sx={{
                  color: 'grey.500',
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                © {new Date().getFullYear()} Bisworupa Inn. All rights reserved.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack
                direction="row"
                spacing={3}
                justifyContent={{ xs: 'center', md: 'flex-end' }}
                alignItems="center"
              >
                <Tooltip title="Multiple Languages Available" arrow>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LanguageIcon sx={{ fontSize: 20, color: 'grey.500' }} />
                    <Typography variant="body2" sx={{ color: 'grey.500' }}>
                      English | हिंदी | ଓଡ଼ିଆ
                    </Typography>
                  </Stack>
                </Tooltip>
                <Tooltip title="Rated 4.8/5 by our guests" arrow>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <StarIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                    <Typography variant="body2" sx={{ color: 'grey.500' }}>
                      4.8/5
                    </Typography>
                  </Stack>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  )
}

export default Footer 